import { useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { useDatasetConfig, useDatasetAtoms } from '../dataset-context';
import { clearedFilters, countActiveFilters } from '../atoms';
import type { DatasetSearchParams } from '../types';

/**
 * Staged-filter lifecycle shared by the filter modal and the filter rail: seed the
 * staged filters from the applied search (which also discards unapplied edits),
 * commit staged filters as a new search, or reset to the dataset defaults.
 * @return {object} The staged filters and their seed/commit/reset.
 */
export function useFilterCommit() {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const [pendingFilters, setPendingFilters] = useAtom(atoms.pendingFiltersAtom);
  const setSearch = useSetAtom(atoms.searchAtom);
  const currentPayload = useAtomValue(atoms.searchAtom).currentPayload;
  const { defaultPayload } = config;

  const seed = useCallback(
    () => setPendingFilters({ ...(currentPayload ?? defaultPayload) }),
    [currentPayload, defaultPayload, setPendingFilters]
  );

  const commit = useCallback(
    (filters: Partial<DatasetSearchParams>) =>
      setSearch(() => ({
        ...defaultPayload,
        ...filters,
        page: 1,
      })),
    [defaultPayload, setSearch]
  );

  // Carries through the applied spatial extent/AOI, text, and plot mode, so a
  // later commit doesn't silently drop them (and desync the radio).
  const reset = useCallback(() => {
    const cleared = clearedFilters(defaultPayload, currentPayload);
    setPendingFilters(cleared);
    return cleared;
  }, [defaultPayload, currentPayload, setPendingFilters]);

  return { pendingFilters, seed, commit, reset };
}

/**
 * How many filters the reader has applied: the applied search's filters beyond
 * those the dataset itself always sends (an entity dataset's entity_id).
 * @return {number} The applied filter count.
 */
export function useAppliedFilterCount(): number {
  const config = useDatasetConfig();
  const atoms = useDatasetAtoms();
  const applied = useAtomValue(atoms.searchAtom).currentPayload;
  return Math.max(
    0,
    countActiveFilters(applied, config) -
      countActiveFilters(config.defaultPayload, config)
  );
}
